import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task, timeout } from 'ember-concurrency';
import { DateTime } from 'scaleflow/utils/date-time';
import nameSort from 'scaleflow/utils/nameSort';
import numberSort from 'scaleflow/utils/numberSort';
import { pluralize } from '@ember-data/request-utils/string';
import { debounceTask } from 'ember-lifeline';
import dateSort from 'scaleflow/utils/dateSort';

export function objectExtract(options, value) {
  return options.find((option) => option.value === value) ?? null;
}

/**
 * @param {Array} list
 * @param {string} none_label
 * @param {string} identifier_key - Dot notation path (e.g., "team.id")
 * @param {string} group_label_key - Dot notation path (e.g., "team.name")
 * @param {Function} object_modifier
 * @param {string} groupingSortKey
 * @param {boolean} hasMany - Whether the relationship is one-to-many
 * @returns {Object}
 */
export function sortInitiatives(
  list,
  none_label,
  identifier_key,
  group_label_key,
  object_modifier,
  groupingSortKey = '',
  hasMany = false,
) {
  const groupedList = {};
  const NONE = {
    title: none_label,
    content: [],
  };

  // Helper function to get value using dot notation
  const getValueByPath = (obj, path) => {
    if (!obj) return null;
    return path
      .split('.')
      .reduce((o, key) => (o && o.get ? o.get(key) : null), obj);
  };

  // Helper function to get pluralized relationship name
  const getPluralizedRelationship = (item, singularKey) => {
    if (!hasMany) return null;

    // Get the base relationship name before the dot
    const baseKey = singularKey.split('.')[0];

    // Ember's convention: pluralize the relationship name for hasMany
    const pluralKey = pluralize(baseKey);

    // Get the collection
    return item.get(pluralKey);
  };
  list.forEach((item) => {
    if (hasMany) {
      // Get the hasMany relationship collection
      const relatedCollection = getPluralizedRelationship(item, identifier_key);
      // If no related items or empty collection, add to NONE group
      if (!relatedCollection || relatedCollection.length === 0) {
        NONE.content.push(item);
        return;
      }

      // Track if the item was added to at least one group
      let addedToGroup = false;

      // For each related item in the collection
      relatedCollection.forEach((relatedItem) => {
        // Get the rest of the path after the relationship name
        const idPathRest = identifier_key.split('.').slice(1).join('.');
        const labelPathRest = group_label_key.split('.').slice(1).join('.');

        // Get ID and label using the rest of the path
        const id = idPathRest
          ? relatedItem.get(idPathRest)
          : relatedItem.get('id');
        const label = labelPathRest
          ? relatedItem.get(labelPathRest)
          : relatedItem.get('name');

        if (!id) return;

        addedToGroup = true;

        // Check if this group already exists
        if (!groupedList[id]) {
          groupedList[id] = {
            title: object_modifier ? object_modifier(relatedItem) : label,
            content: [],
            ...(object_modifier &&
              groupingSortKey && {
                [groupingSortKey]:
                  object_modifier(relatedItem)[groupingSortKey] ?? Infinity,
              }),
          };
        }

        // Only add the item if it's not already in the group
        if (!groupedList[id].content.includes(item)) {
          groupedList[id].content.push(item);
        }
      });

      // If item wasn't added to any group, add to NONE
      if (!addedToGroup) {
        NONE.content.push(item);
      }
    } else {
      // Original one-to-one logic using dot notation
      const id = getValueByPath(item, identifier_key);

      if (!id) {
        NONE.content.push(item);
        return;
      }

      const label = getValueByPath(item, group_label_key);

      const ref = groupedList[id];
      if (ref && ref.content) {
        ref.content.push(item);
      } else {
        groupedList[id] = {
          title: object_modifier ? object_modifier(id) : label,
          content: [item],
          ...(object_modifier &&
            groupingSortKey && {
              [groupingSortKey]:
                object_modifier(id)[groupingSortKey] ?? Infinity,
            }),
        };
      }
    }
  });

  // Sorting logic remains the same
  const sortedGroupList = {};
  if (groupingSortKey) {
    const sorted = Object.keys(groupedList).sort((a, b) =>
      numberSort(
        groupedList[a][groupingSortKey],
        groupedList[b][groupingSortKey],
        'asc',
      ),
    );
    sorted.forEach((key) => {
      sortedGroupList[key] = groupedList[key];
    });
  } else {
    const sorted = Object.keys(groupedList).sort((a, b) =>
      nameSort(groupedList[a]['title'], groupedList[b]['title']),
    );
    sorted.forEach((key) => {
      sortedGroupList[key] = groupedList[key];
    });
  }

  if (NONE.content.length !== 0) {
    sortedGroupList.NONE = NONE;
  }

  return sortedGroupList;
}

export default class InitiativesTableComponent extends Component {
  @service router;
  @service favorite;
  @service store;
  @service('beta-toggle') betaService;
  @service('organisation') orgService;
  @service intl;
  @service initiativeSlideover;
  @service('dropdown') dropdownService;
  @service tracker;

  defaultStatus = [
    'ACTIVE',
    'READY',
    'READY FOR PLANNING',
    'DISCOVERY',
    'DRAFT',
  ];

  @tracked list = this.args.list ?? [];

  @tracked grouping = this.args.grouping ?? 'none';
  @tracked statuses = this.args.filters?.statuses ?? this.defaultStatus;
  @tracked themes = this.args.filters?.themes ?? [];
  @tracked sources = this.args.filters?.sources ?? [];
  @tracked productManagers = this.args.filters?.product_managers ?? [];
  @tracked teams = this.args.filters?.teams ?? [];
  @tracked roadmaps = this.args.filters?.roadmaps ?? [];
  @tracked goals = this.args.filters?.goals ?? [];
  @tracked search = this.args.search ?? '';
  @tracked columns = this.args.filters.columns?.length
    ? this.args.filters.columns
    : [];

  @tracked tableSort = this.args.tableSort ?? '';

  @tracked processOngoing = false;

  tableId = 'initiatives-table';

  get filters() {
    return {
      statuses: this.statuses,
      sources: this.sources,
      theme: this.themes,
      product_managers: this.productManagers,
      teams: this.teams,
      roadmaps: this.roadmaps,
      goals: this.goals,
    };
  }

  get initiativeFormIsVisible() {
    return this.args.initiativeFormIsVisible;
  }

  @action
  toggleInitiativeForm() {
    this.args.toggleInitiativeForm();
    this.fetchInitiatives.perform();
  }

  get initiatives() {
    const search = this.args.externallyControlledSearch
      ? this.args.search
      : this.search;
    let initiatives = this.list
      .filter((item) => !item.isNew)
      .filter(
        (item) =>
          item.title.toLowerCase().includes((search ?? '').toLowerCase()) ||
          item?.abbreviation
            ?.toLowerCase?.()
            .includes?.((search ?? '').toLowerCase()),
      )
      .sort((a, b) => this.startingDateSort(a.startDate, b.startDate));

    return initiatives;
  }

  get sortSettings() {
    const [sortKey = '', sortOrder = ''] = this.tableSort.split('|');
    return { sortKey, sortOrder };
  }

  get groupedInitiatives() {
    let list = this.initiatives;
    let groupedSort =
      this.grouping !== 'none'
        ? this.handleGrouping(list, this.grouping)
        : null;
    const { sortKey, sortOrder } = this.sortSettings;
    switch (sortKey) {
      case 'title': {
        groupedSort
          ? Object.keys(groupedSort).forEach((group) => {
              groupedSort[group].content.sort((a, b) =>
                nameSort(a.title, b.title, sortOrder),
              );
            })
          : list.sort((a, b) => nameSort(a.title, b.title, sortOrder));
        break;
      }

      case 'abbreviation': {
        groupedSort
          ? Object.keys(groupedSort).forEach((group) => {
              groupedSort[group].content.sort((a, b) =>
                nameSort(a.abbreviation, b.abbreviation, sortOrder),
              );
            })
          : list.sort((a, b) =>
              nameSort(a.abbreviation, b.abbreviation, sortOrder),
            );
        break;
      }

      case 'source': {
        groupedSort
          ? Object.keys(groupedSort).forEach((group) => {
              groupedSort[group].content.sort((a, b) =>
                nameSort(a.sourceLabel, b.sourceLabel, sortOrder),
              );
            })
          : list.sort((a, b) =>
              nameSort(a.sourceLabel, b.sourceLabel, sortOrder),
            );
        break;
      }
      case 'status': {
        groupedSort
          ? Object.keys(groupedSort).forEach((group) => {
              groupedSort[group].content.sort((a, b) =>
                nameSort(a.statusLabel, b.statusLabel, sortOrder),
              );
            })
          : list.sort((a, b) =>
              nameSort(a.statusLabel, b.statusLabel, sortOrder),
            );
        break;
      }

      case 'bon_investment': {
        groupedSort
          ? Object.keys(groupedSort).forEach((group) => {
              groupedSort[group].content.sort((a, b) =>
                numberSort(
                  +(a?.bon_investment ?? 0),
                  +(b?.bon_investment ?? 0),
                  sortOrder,
                ),
              );
            })
          : list.sort((a, b) =>
              numberSort(
                +(a?.bon_investment ?? 0),
                +(b?.bon_investment ?? 0),
                sortOrder,
              ),
            );
        break;
      }
      case 'bon_value': {
        groupedSort
          ? Object.keys(groupedSort).forEach((group) => {
              groupedSort[group].content.sort((a, b) =>
                numberSort(
                  +(a?.bon_value ?? -1),
                  +(b?.bon_value ?? -1),
                  sortOrder,
                ),
              );
            })
          : list.sort((a, b) =>
              numberSort(
                +(a?.bon_value ?? -1),
                +(b?.bon_value ?? -1),
                sortOrder,
              ),
            );
        break;
      }
      case 'bon_returns': {
        groupedSort
          ? Object.keys(groupedSort).forEach((group) => {
              groupedSort[group].content.sort((a, b) =>
                numberSort(
                  +(a?.bon_returns ?? -1),
                  +(b?.bon_returns ?? -1),
                  sortOrder,
                ),
              );
            })
          : list.sort((a, b) =>
              numberSort(
                +(a?.bon_returns ?? -1),
                +(b?.bon_returns ?? -1),
                sortOrder,
              ),
            );
        break;
      }
      case 'person_weeks': {
        groupedSort
          ? Object.keys(groupedSort).forEach((group) => {
              groupedSort[group].content.sort((a, b) =>
                numberSort(
                  +(a?.person_weeks ?? -1),
                  +(b?.person_weeks ?? -1),
                  sortOrder,
                ),
              );
            })
          : list.sort((a, b) =>
              numberSort(
                +(a?.person_weeks ?? -1),
                +(b?.person_weeks ?? -1),
                sortOrder,
              ),
            );
        break;
      }
      case 'person_weeks_only_uncertainty': {
        groupedSort
          ? Object.keys(groupedSort).forEach((group) => {
              groupedSort[group].content.sort((a, b) =>
                numberSort(
                  +(a?.person_weeks_only_uncertainty ?? -1),
                  +(b?.person_weeks_only_uncertainty ?? -1),
                  sortOrder,
                ),
              );
            })
          : list.sort((a, b) =>
              numberSort(
                +(a?.person_weeks_only_uncertainty ?? -1),
                +(b?.person_weeks_only_uncertainty ?? -1),
                sortOrder,
              ),
            );
        break;
      }

      case 'productManager': {
        groupedSort
          ? Object.keys(groupedSort).forEach((group) => {
              groupedSort[group].content.sort((a, b) =>
                nameSort(
                  a?.get('productManager.name') ?? '',
                  b?.get('productManager.name') ?? '',
                  sortOrder,
                ),
              );
            })
          : list.sort((a, b) =>
              nameSort(
                a?.get('productManager.name') ?? '',
                b?.get('productManager.name') ?? '',
                sortOrder,
              ),
            );
        break;
      }

      case 'team': {
        groupedSort
          ? Object.keys(groupedSort).forEach((group) => {
              groupedSort[group].content.sort((a, b) =>
                nameSort(
                  a?.get('firstTeam.name') ?? '',
                  b?.get('firstTeam.name') ?? '',
                  sortOrder,
                ),
              );
            })
          : list.sort((a, b) =>
              nameSort(
                a?.get('firstTeam.name') ?? '',
                b?.get('firstTeam.name') ?? '',
                sortOrder,
              ),
            );
        break;
      }

      case 'roadmap': {
        groupedSort
          ? Object.keys(groupedSort).forEach((group) => {
              groupedSort[group].content.sort((a, b) =>
                nameSort(
                  a?.get('firstRoadmap.title') ?? '',
                  b?.get('firstRoadmap.title') ?? '',
                  sortOrder,
                ),
              );
            })
          : list.sort((a, b) =>
              nameSort(
                a?.get('firstRoadmap.title') ?? '',
                b?.get('firstRoadmap.title') ?? '',
                sortOrder,
              ),
            );
        break;
      }

      case 'goal': {
        groupedSort
          ? Object.keys(groupedSort).forEach((group) => {
              groupedSort[group].content.sort((a, b) =>
                nameSort(
                  a?.get('firstGoal.title') ?? '',
                  b?.get('firstGoal.title') ?? '',
                  sortOrder,
                ),
              );
            })
          : list.sort((a, b) =>
              nameSort(
                a?.get('firstGoal.title') ?? '',
                b?.get('firstGoal.title') ?? '',
                sortOrder,
              ),
            );
        break;
      }

      case 'theme': {
        groupedSort
          ? Object.keys(groupedSort).forEach((group) => {
              groupedSort[group].content.sort((a, b) =>
                nameSort(
                  a?.get('theme.title') ?? '',
                  b?.get('theme.title') ?? '',
                  sortOrder,
                ),
              );
            })
          : list.sort((a, b) =>
              nameSort(
                a?.get('theme.title') ?? '',
                b?.get('theme.title') ?? '',
                sortOrder,
              ),
            );
        break;
      }

      case 'starting_week': {
        groupedSort
          ? Object.keys(groupedSort).forEach((group) => {
              groupedSort[group].content.sort((a, b) =>
                dateSort(a?.startDate ?? '', b?.startDate ?? '', sortOrder),
              );
            })
          : list.sort((a, b) =>
              dateSort(a?.startDate ?? '', b?.startDate ?? '', sortOrder),
            );
        break;
      }

      case 'duration': {
        groupedSort
          ? Object.keys(groupedSort).forEach((group) => {
              groupedSort[group].content.sort((a, b) =>
                numberSort(
                  a?.get('duration') ?? 0,
                  b?.get('duration') ?? 0,
                  sortOrder,
                ),
              );
            })
          : list.sort((a, b) =>
              numberSort(
                a?.get('duration') ?? 0,
                b?.get('duration') ?? 0,
                sortOrder,
              ),
            );
        break;
      }

      case 'progress': {
        groupedSort
          ? Object.keys(groupedSort).forEach((group) => {
              groupedSort[group].content.sort((a, b) =>
                nameSort(
                  a?.get('progressLabel') ?? '',
                  b?.get('progressLabel') ?? '',
                  sortOrder,
                ),
              );
            })
          : list.sort((a, b) =>
              nameSort(
                a?.get('progressLabel') ?? '',
                b?.get('progressLabel') ?? '',
                sortOrder,
              ),
            );
        break;
      }

      case 'team_effort': {
        groupedSort
          ? Object.keys(groupedSort).forEach((group) => {
              groupedSort[group].content.sort((a, b) =>
                numberSort(
                  +(a?.person_weeks ?? -1),
                  +(b?.person_weeks ?? -1),
                  sortOrder,
                ),
              );
            })
          : list.sort((a, b) =>
              numberSort(
                +(a?.person_weeks ?? -1),
                +(b?.person_weeks ?? -1),
                sortOrder,
              ),
            );
        break;
      }

      default:
        list.sort((a, b) => nameSort(a.title, b.title));
        break;
    }
    return groupedSort ? groupedSort : list;
  }

  @action
  startingDateSort(a_starting_date, b_starting_date) {
    if (!a_starting_date) return 1;
    if (!b_starting_date) return 1;

    const aDate = DateTime.from(a_starting_date);
    const bDate = DateTime.from(b_starting_date);

    if (aDate.isAfter(bDate)) return 1;
    if (bDate.isAfter(aDate)) return -1;
    return 0;
  }

  get statusMap() {
    return this.betaService.statusOptions.reduce((acc, item) => {
      acc[item.value] = item.tableOrder;
      return acc;
    }, {});
  }

  @action
  sortStatus(a, b) {
    return this.statusMap[a] - this.statusMap[b]; // Sort alphabetically for other elements
  }

  @action
  handleGrouping(list, groupKey) {
    switch (groupKey) {
      case 'statuses': {
        const initiatives = sortInitiatives(
          list,
          'No Status',
          'status',
          'status',
          (value) => {
            return objectExtract(this.betaService.statusOptions, value)?.label;
          },
          'tableOrder',
        );
        const sortedRows = Object.keys(initiatives)
          .sort(this.sortStatus)
          .map((item) => {
            return initiatives[item];
          });
        return sortedRows;
      }

      case 'sources': {
        return sortInitiatives(
          list,
          'No Source',
          'source',
          'source',
          (value) =>
            objectExtract(this.betaService.sourceOptions, value)?.label,
          'tableOrder',
        );
      }

      case 'productManagers': {
        return sortInitiatives(
          list,
          'No PM',
          'productManager.id',
          'productManager.name',
        );
      }

      case 'teams': {
        return sortInitiatives(
          list,
          'No Team',
          'team.id',
          'team.name',
          undefined,
          undefined,
          true,
        );
      }

      case 'themes': {
        return sortInitiatives(list, 'No Theme', 'theme.id', 'theme.title');
      }

      case 'progress': {
        return sortInitiatives(
          list,
          'No Update',
          'progress',
          'progressLabel',
          (val) => this.intl.t(`_initiatives.updates_${val?.toLowerCase()}`),
        );
      }

      case 'roadmaps': {
        return sortInitiatives(
          list,
          'No Roadmap',
          'roadmap.id',
          'roadmap.title',
          undefined,
          undefined,
          true,
        );
      }

      case 'goals': {
        return sortInitiatives(
          list,
          'No Goal',
          'goal.id',
          'goal.title',
          undefined,
          undefined,
          true,
        );
      }

      case 'durations': {
        return sortInitiatives(
          list,
          'No Duration',
          'duration',
          'durationLabel',
          undefined,
          undefined,
        );
      }

      default: {
        return sortInitiatives(list, 'No Status', 'status', 'status');
      }
    }
  }

  @action
  onSortSelect(key, order) {
    const sort = `${key}|${order}`;
    this.tableSort = this.tableSort === sort ? '' : sort;
    this.pushToUrlParams('tableSort', this.tableSort);
    this.emitTableInteractionEvent();
  }

  @action
  scrollTableToTop() {
    document.querySelector(`#${this.tableId}`).scrollTop = 0;
  }

  fetchInitiatives = task(
    { restartable: true, maxConcurrency: 1 },
    async () => {
      this.scrollTableToTop();
      this.processOngoing = true;
      await timeout(500);
      let initiatives = [];
      const params = { ...this.filters };
      delete params['columns'];
      delete params['grouping'];
      let status = params.statuses;
      delete params.statuses;
      if (status == undefined || status.length === 0)
        status = this.defaultStatus;
      initiatives = await this.store.query('initiative', {
        ...params,
        status,
        include: 'workstream',
      });
      this.list = initiatives;
      this.processOngoing = false;
    },
  );

  @action
  emitTableInteractionEvent() {
    this.tracker.trackInitiativesTableInteractionEvent();
  }

  @action
  showColumn(column) {
    if ((this.columns?.length ?? 0) === 0) {
      if (column.show !== undefined) return column.show;
      return true;
    } else {
      return this.columns.includes(column.label);
    }
  }

  get columnOptions() {
    const columns = this.dropdownService.columnsList;
    columns.forEach((item) => {
      item.show = this.showColumn(item);
    });
    return columns.slice();
  }

  @action
  calculateInvestmentInPersonWeeks(investment) {
    if (
      investment.people == undefined ||
      investment.weeks == undefined ||
      investment.confidence_value == undefined
    )
      return;
    return (
      investment.people *
      investment.weeks *
      investment.confidence_value
    ).toFixed(1);
  }

  @action
  onInitiativeAdded(initiative) {
    this.args?.onInitiativeAdded?.(initiative);
  }

  @action
  onFilterSelect(key, e) {
    this[key] = e;
    debounceTask(this, 'pushToUrlParams', key, e, 1500);
  }

  @action
  setFilters() {
    const filters = this.args.filters ?? {};
    Object.keys(filters).forEach((key) => {
      if (filters[key]) this[key] = filters[key];
    });
  }

  @action
  onGroupingSelect(e) {
    this.grouping = this.grouping === e.value ? 'none' : e.value;
    debounceTask(this, 'pushToUrlParams', 'grouping', this.grouping, 1500);
  }

  get disabledColumns() {
    return this.args?.disabledColumns ?? [];
  }

  @action
  setSearch(e) {
    this.search = e;
    debounceTask(this, 'pushToUrlParams', 'search', e, 1500);
  }

  @action
  hideColumn(column, allColumns) {
    let values = [...allColumns];
    if (values.includes(column)) {
      values = values.filter((_column) => _column !== column);
    } else {
      values.push(column);
    }
    this.onColumnSelect(values);
  }
  @action
  onColumnSelect(values) {
    this.columns = values;
    debounceTask(this, 'pushToUrlParams', 'columns', values, 500);
  }

  @action
  pushToUrlParams(key, value) {
    this.router.transitionTo({ queryParams: { [key]: value } });
  }

  @action
  anyDropdownFeatureIsAvailable(column) {
    return (
      column.showGrouping ||
      column.showSorting ||
      column.dropdownDataKey ||
      column.showVisibilityToggle
    );
  }

  willDestroy() {
    this.list = [];
    this.processOngoing = false;
    this.tableSort = '';
    this.fetchInitiatives.cancelAll();
    super.willDestroy(...arguments);
  }
}
